import React, {
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { ReactSVG } from "react-svg";
import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-m";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import styles from "./InlineMediaInfo.module.scss";
import { StepZutat } from "@/types/baseTypes";
import RecipeIngredientTable from "../RecipeComponents/RecipeSingleView/RecipeIngredientList/RecipeIngredientTable/RecipeIngredientTable";

type InlineMediaInfoProps = {
  description: string;
  step_zutaten?: StepZutat[];
  defaultOpen?: boolean;
  className?: string;
  idBase?: string; // Allows to set a custom base ID for accessibility attributes
  focusOnOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const InlineMediaInfo = ({
  description,
  defaultOpen = false,
  className,
  idBase,
  focusOnOpen = true,
  onOpenChange,
  step_zutaten,
}: InlineMediaInfoProps) => {
  const { getIcon, getLabel } = useGlobalContext();
  const [open, setOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const reactId = useId();
  const base = idBase ?? reactId;

  const panelId = `${base}-media-info-panel`;
  const titleId = `${base}-media-info-title`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const wrapper = root.closest<HTMLElement>("figure.image-wrapper");
    if (!wrapper) return;

    const apply = () => {
      const h = wrapper.getBoundingClientRect().height + 10; //padding rounded corners
      root.style.setProperty("--media-h", `${h}px`);
    };

    const ro = new ResizeObserver(apply);
    ro.observe(wrapper);
    apply();

    return () => ro.disconnect();
  }, []);

  const openInfo = useCallback(() => {
    setOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const closeInfo = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  // Esc for closing
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        closeInfo();
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeInfo]);

  // Move focus to close button when open
  useEffect(() => {
    if (open && focusOnOpen) {
      const t = setTimeout(() => closeRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [open, focusOnOpen]);

  return (
    <div
      ref={rootRef}
      className={`${styles["inline-media-info"]} ${className ?? ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`${styles["media-info-button"]} button-reset`}
        aria-label={getLabel("aria_label_media_info_button")}
        aria-controls={panelId}
        aria-expanded={open}
        onClick={() => (open ? closeInfo() : openInfo())}
      >
        <ReactSVG
          src={getIcon("info")}
          wrapper="svg"
          width={16}
          height={16}
          beforeInjection={(svg) => {
            svg.setAttribute("alt", "info icon");
            svg.setAttribute("aria-label", "info icon");
          }}
        />
      </button>

      <AnimatePresence initial={false}>
        <motion.div
          key="wrapper"
          initial={{ height: 0, overflow: "hidden" }}
          animate={{
            height: open ? "auto" : 0,
            overflow: open ? "visible" : "hidden",
          }}
          exit={{ height: 0, overflow: "hidden" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          aria-hidden={!open}
          data-lenis-prevent
          data-lenis-prevent-wheel
          data-lenis-prevent-touch
        >
          <motion.section
            id={panelId}
            role="region"
            aria-labelledby={titleId}
            className={styles["media-info-panel"]}
            animate={{
              y: open ? 0 : "100%",
              opacity: open ? 1 : 0,
              padding: open ? "3rem 2rem 2rem" : 0,
              overflowY: open ? "auto" : "hidden",
              zIndex: open ? 1 : -1,
            }}
            data-lenis-prevent
            data-lenis-prevent-wheel
            data-lenis-prevent-touch
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <button
              ref={closeRef}
              type="button"
              className={`${styles["close-btn"]} button-reset`}
              aria-label={getLabel("aria_label_media_info_close_button")}
              onClick={() => {
                closeInfo();
                requestAnimationFrame(() => triggerRef.current?.focus());
              }}
            >
              <ReactSVG
                src={getIcon("close-circle")}
                wrapper="svg"
                width={27}
                height={27}
                beforeInjection={(svg) => {
                  {
                    /* jscpd:ignore-start */
                  }

                  svg.setAttribute("alt", "close icon");
                  svg.setAttribute("aria-label", "close icon");
                  {
                    /* jscpd:ignore-end */
                  }
                }}
              />
            </button>
            {step_zutaten && step_zutaten.length > 0 && (
              <RecipeIngredientTable ingredients={step_zutaten} isSmall />
            )}
            <ContentTag
              extraClass={`${step_zutaten && step_zutaten.length > 0 ? "mt-4" : ""}`}
              content={description}
              showTitle={false}
            />
          </motion.section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default InlineMediaInfo;
